import React, { useState, useMemo, useCallback } from 'react';
import { Plus, RefreshCw, ChevronDown, ChevronUp, Package, Search, ArrowUpDown, ArrowUp, ArrowDown, Calendar, X, CheckSquare } from 'lucide-react';
import { MktBadge, Badge, Th, Td, formatDT } from '../WMSShared';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api/wms';
const STATUSES = ['processing','packed','shipped','in_transit','delivered','cancelled','returned','refunded'];
const MARKETPLACES = ['shopee','shopify','ebay','carousell','woocommerce'];
const DATE_PRESETS = [
  { label: 'Today', days: 0 },
  { label: 'This Week', days: 7 },
  { label: 'This Month', days: 30 },
  { label: '3 Months', days: 90 },
  { label: 'All Time', days: null },
];

function PayBadge({ status }) {
  const c = status === 'paid' ? 'bg-emerald-100 text-emerald-700' : status === 'refunded' ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600';
  return <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase ${c}`}>{status || 'paid'}</span>;
}

export default function OrdersTab({ orders, load, setEditItem, setForm, setOrderItems, setModal, orderStatus, cur = 'S$', tz = 'Asia/Singapore' }) {
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(null);

  const handleStatusChange = async (orderId, newStatus) => {
    setStatusUpdating(orderId);
    await orderStatus(orderId, newStatus);
    setStatusUpdating(null);
  };

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [mktFilter, setMktFilter] = useState('');
  const [datePreset, setDatePreset] = useState(null);

  // Sort
  const [sortCol, setSortCol] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  // Bulk
  const [selected, setSelected] = useState(new Set());
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);

  const syncFromPlatforms = async () => {
    setSyncing(true);
    setSyncMsg('');
    try {
      const r = await axios.post(`${API}/connectors/sync-all-orders`);
      const platforms = Object.keys(r.data.synced || {});
      if (platforms.length === 0) {
        setSyncMsg('No connectors configured — go to Import & Connect.');
      } else {
        const counts = platforms.map(p => {
          const res = r.data.synced[p];
          return res.error ? `${p}: ${res.error}` : `${p}: ${res.imported || 0} new`;
        }).join(' · ');
        setSyncMsg(counts);
      }
    } catch {
      setSyncMsg('Sync failed — check connector credentials');
    }
    await load();
    setSyncing(false);
    setTimeout(() => setSyncMsg(''), 5000);
  };

  // Filter + Sort logic
  const filtered = useMemo(() => {
    let list = [...orders];

    if (search) {
      const q = search.toLowerCase();
      list = list.filter(o =>
        (o.order_id || '').toLowerCase().includes(q) ||
        (o.customer_name || '').toLowerCase().includes(q) ||
        (o.customer_email || '').toLowerCase().includes(q)
      );
    }
    if (statusFilter) list = list.filter(o => o.status === statusFilter);
    if (mktFilter) list = list.filter(o => (o.marketplace || '').toLowerCase() === mktFilter);
    if (datePreset !== null) {
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - datePreset);
      cutoff.setHours(0, 0, 0, 0);
      list = list.filter(o => new Date(o.created_at) >= cutoff);
    }

    list.sort((a, b) => {
      let va, vb;
      if (sortCol === 'created_at') { va = new Date(a.created_at || 0); vb = new Date(b.created_at || 0); }
      else if (sortCol === 'total') { va = a.grand_total || a.total || 0; vb = b.grand_total || b.total || 0; }
      else if (sortCol === 'qty') { va = (a.items || []).reduce((s, i) => s + (i.quantity || 1), 0); vb = (b.items || []).reduce((s, i) => s + (i.quantity || 1), 0); }
      else if (sortCol === 'status') { va = a.status || ''; vb = b.status || ''; }
      else if (sortCol === 'customer') { va = (a.customer_name || '').toLowerCase(); vb = (b.customer_name || '').toLowerCase(); }
      else { va = a[sortCol] || ''; vb = b[sortCol] || ''; }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [orders, search, statusFilter, mktFilter, datePreset, sortCol, sortDir]);

  // Status summary counts
  const statusCounts = useMemo(() => {
    const c = {};
    orders.forEach(o => { c[o.status] = (c[o.status] || 0) + 1; });
    return c;
  }, [orders]);

  const toggleSort = useCallback((col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('desc'); }
  }, [sortCol]);

  const toggleRow = (id) => setExpanded(prev => prev === id ? null : id);

  const toggleSelect = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(o => o.order_id)));
  };

  const bulkUpdate = async () => {
    if (!bulkStatus || selected.size === 0) return;
    setBulkLoading(true);
    try {
      await axios.put(`${API}/orders/bulk-status`, { order_ids: [...selected], status: bulkStatus });
      setSelected(new Set());
      setBulkStatus('');
      await load();
    } catch {}
    setBulkLoading(false);
  };

  const hasFilters = search || statusFilter || mktFilter || datePreset !== null;
  const clearFilters = () => { setSearch(''); setStatusFilter(''); setMktFilter(''); setDatePreset(null); };

  const SortHeader = ({ col, children, className = '' }) => {
    const active = sortCol === col;
    return (
      <th className={`px-2 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-800 ${className}`}
          onClick={() => toggleSort(col)} data-testid={`sort-${col}`}>
        <div className="flex items-center gap-1">
          {children}
          {active ? (sortDir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)
                  : <ArrowUpDown className="w-3 h-3 opacity-30" />}
        </div>
      </th>
    );
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold">
          Orders <span className="text-gray-400 font-normal text-base">({orders.length})</span>
        </h2>
        <div className="flex items-center gap-3">
          {syncMsg && <span className="text-xs text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-lg">{syncMsg}</span>}
          <button onClick={syncFromPlatforms} disabled={syncing}
            className="flex items-center gap-2 text-sm text-gray-600 border px-3 py-2 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            data-testid="sync-orders-btn">
            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync'}
          </button>
          <button
            onClick={() => { setEditItem(null); setForm({ marketplace: 'shopee', shipping_city: 'Singapore' }); setOrderItems([]); setModal('order'); }}
            className="flex items-center gap-2 bg-blue-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-600 shadow-sm"
            data-testid="new-order-btn">
            <Plus className="w-4 h-4" />New Order
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="order-status-summary">
        {['processing','packed','shipped','delivered','cancelled','returned'].map(s => {
          const count = statusCounts[s] || 0;
          if (!count && s !== 'processing') return null;
          const colors = {
            processing: 'bg-amber-50 text-amber-700 border-amber-200',
            packed: 'bg-blue-50 text-blue-700 border-blue-200',
            shipped: 'bg-indigo-50 text-indigo-700 border-indigo-200',
            delivered: 'bg-emerald-50 text-emerald-700 border-emerald-200',
            cancelled: 'bg-red-50 text-red-600 border-red-200',
            returned: 'bg-orange-50 text-orange-700 border-orange-200',
          };
          return (
            <button key={s} onClick={() => setStatusFilter(statusFilter === s ? '' : s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${colors[s] || 'bg-gray-50 text-gray-600 border-gray-200'} ${statusFilter === s ? 'ring-2 ring-offset-1 ring-blue-400' : ''}`}
              data-testid={`filter-status-${s}`}>
              {count} {s.charAt(0).toUpperCase() + s.slice(1).replace('_', ' ')}
            </button>
          );
        })}
      </div>

      {/* Search + Filters Bar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" placeholder="Search order ID, customer..." value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-300 focus:border-blue-400 outline-none"
            data-testid="order-search" />
        </div>
        <select value={mktFilter} onChange={e => setMktFilter(e.target.value)}
          className="text-sm border rounded-lg px-3 py-2 bg-white" data-testid="filter-marketplace">
          <option value="">All Marketplaces</option>
          {MARKETPLACES.map(m => <option key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</option>)}
        </select>
        <div className="flex items-center gap-1 bg-gray-50 rounded-lg border px-1 py-0.5">
          <Calendar className="w-3.5 h-3.5 text-gray-400 ml-1.5" />
          {DATE_PRESETS.map(p => (
            <button key={p.label} onClick={() => setDatePreset(datePreset === p.days ? null : p.days)}
              className={`text-[11px] px-2 py-1 rounded-md font-medium transition-all ${datePreset === p.days ? 'bg-white shadow-sm text-blue-700' : 'text-gray-500 hover:text-gray-800'}`}
              data-testid={`filter-date-${p.label.toLowerCase().replace(' ','-')}`}>
              {p.label}
            </button>
          ))}
        </div>
        {hasFilters && (
          <button onClick={clearFilters} className="text-xs text-gray-500 hover:text-red-600 flex items-center gap-1 px-2 py-1" data-testid="clear-filters">
            <X className="w-3.5 h-3.5" /> Clear
          </button>
        )}
      </div>

      {/* Bulk Actions Bar */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 mb-3 px-4 py-2.5 bg-blue-50 border border-blue-200 rounded-lg" data-testid="bulk-actions-bar">
          <CheckSquare className="w-4 h-4 text-blue-600" />
          <span className="text-sm font-semibold text-blue-800">{selected.size} selected</span>
          <select value={bulkStatus} onChange={e => setBulkStatus(e.target.value)}
            className="text-xs border rounded-lg px-2 py-1.5 bg-white" data-testid="bulk-status-select">
            <option value="">Change status to...</option>
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <button onClick={bulkUpdate} disabled={!bulkStatus || bulkLoading}
            className="text-xs bg-blue-500 text-white px-3 py-1.5 rounded-lg font-medium hover:bg-blue-600 disabled:opacity-50"
            data-testid="bulk-update-btn">
            {bulkLoading ? 'Updating...' : 'Apply'}
          </button>
          <button onClick={() => setSelected(new Set())} className="text-xs text-gray-500 hover:text-gray-800 ml-auto">Deselect all</button>
        </div>
      )}

      {/* Results count */}
      {hasFilters && (
        <p className="text-xs text-gray-500 mb-2">Showing {filtered.length} of {orders.length} orders</p>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm table-fixed">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 py-3 w-8">
                  <input type="checkbox" checked={selected.size > 0 && selected.size === filtered.length}
                    onChange={toggleAll} className="rounded border-gray-300" data-testid="select-all" />
                </th>
                <SortHeader col="order_id">Order ID</SortHeader>
                <SortHeader col="created_at">Date</SortHeader>
                <SortHeader col="customer">Customer</SortHeader>
                <Th>Mkt</Th>
                <Th>Products</Th>
                <SortHeader col="qty" className="text-center">Qty</SortHeader>
                <SortHeader col="total">Total</SortHeader>
                <SortHeader col="status">Status</SortHeader>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.map(o => {
                const items = o.items || [];
                const totalQty = items.reduce((sum, i) => sum + (i.quantity || 1), 0);
                const isOpen = expanded === o.order_id;
                const isSelected = selected.has(o.order_id);
                return (
                  <React.Fragment key={o.order_id}>
                    <tr className={`hover:bg-gray-50 cursor-pointer transition-colors ${isOpen ? 'bg-blue-50/50' : ''} ${isSelected ? 'bg-blue-50/30' : ''}`}
                        onClick={() => toggleRow(o.order_id)} data-testid={`order-row-${o.order_id}`}>
                      <Td onClick={e => e.stopPropagation()}>
                        <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(o.order_id)}
                          className="rounded border-gray-300" data-testid={`select-order-${o.order_id}`} />
                      </Td>
                      <Td>
                        <div className="flex items-center gap-1">
                          {isOpen ? <ChevronUp className="w-3 h-3 text-blue-500 shrink-0" /> : <ChevronDown className="w-3 h-3 text-gray-400 shrink-0" />}
                          <span className="font-mono text-[11px]">{o.order_id}</span>
                        </div>
                      </Td>
                      <Td className="text-gray-500 text-xs whitespace-nowrap">{formatDT(o.created_at, tz)}</Td>
                      <Td>
                        <p className="font-medium text-xs truncate max-w-[120px]">{o.customer_name}</p>
                      </Td>
                      <Td><MktBadge m={o.marketplace} /></Td>
                      <Td>
                        {(() => {
                          if (items.length === 0) return <span className="text-gray-300">—</span>;
                          const first = items[0].name || items[0].sku || '?';
                          const truncated = first.length > 20 ? first.slice(0, 20) + '...' : first;
                          if (items.length === 1) return <span className="text-xs">{truncated}</span>;
                          return <span className="text-xs">{truncated}<span className="text-gray-400"> +{items.length - 1}</span></span>;
                        })()}
                      </Td>
                      <Td className="text-center font-semibold text-xs">{totalQty}</Td>
                      <Td className="font-semibold text-xs">{cur}{(o.grand_total || o.total || 0).toLocaleString()}</Td>
                      <Td><Badge status={o.status} /></Td>
                      <Td onClick={e => e.stopPropagation()}>
                        <div className="relative">
                          <select className={`text-xs border rounded px-2 py-1 bg-white ${statusUpdating === o.order_id ? 'opacity-50 pointer-events-none' : ''}`}
                            value={o.status} onChange={e => handleStatusChange(o.order_id, e.target.value)}
                            disabled={statusUpdating === o.order_id}
                            data-testid={`order-status-${o.order_id}`}>
                            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                          {statusUpdating === o.order_id && (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <svg className="animate-spin h-3.5 w-3.5 text-blue-500" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                            </div>
                          )}
                        </div>
                      </Td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-gray-50/80">
                        <td colSpan={10} className="px-6 py-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Line Items */}
                            <div>
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                                <Package className="w-3.5 h-3.5" /> Line Items ({items.length})
                              </p>
                              {items.length > 0 ? (
                                <div className="bg-white rounded-lg border divide-y">
                                  {items.map((item, idx) => (
                                    <div key={idx} className="flex items-center justify-between px-3 py-2.5 text-xs">
                                      <div className="flex-1 min-w-0">
                                        <p className="font-medium text-gray-800 truncate">{item.name || item.sku || 'Unknown'}</p>
                                        {item.sku && <p className="text-[10px] text-gray-400 mt-0.5">SKU: {item.sku}</p>}
                                      </div>
                                      <div className="flex items-center gap-4 shrink-0 text-right">
                                        <span className="text-gray-500">x{item.quantity || 1}</span>
                                        <span className="font-semibold w-20 text-right">{cur}{(item.price || 0).toLocaleString()}</span>
                                        <span className="font-bold text-gray-800 w-24 text-right">{cur}{((item.price || 0) * (item.quantity || 1)).toLocaleString()}</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-xs text-gray-400">No line items</p>
                              )}
                            </div>
                            {/* Order Info */}
                            <div>
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Order Details</p>
                              <div className="bg-white rounded-lg border p-3 space-y-2 text-xs">
                                <InfoRow label="Order ID" value={o.order_id} mono />
                                <InfoRow label="Customer" value={o.customer_name} />
                                {o.customer_email && <InfoRow label="Email" value={o.customer_email} />}
                                {o.customer_phone && <InfoRow label="Phone" value={o.customer_phone} />}
                                <InfoRow label="Marketplace" value={o.marketplace} />
                                <InfoRow label="Payment" value={o.payment_status || 'paid'} />
                                {o.shipping_address && <InfoRow label="Shipping" value={`${o.shipping_address}${o.shipping_city ? ', ' + o.shipping_city : ''}${o.shipping_postal ? ' ' + o.shipping_postal : ''}`} />}
                                {o.tracking_number && o.tracking_number !== '-' && <InfoRow label="Tracking" value={o.tracking_number} mono />}
                                <div className="border-t pt-2 mt-2">
                                  <InfoRow label="Subtotal" value={`${cur}${(o.subtotal || o.total || 0).toLocaleString()}`} />
                                  {(o.shipping_fee > 0) && <InfoRow label="Shipping" value={`${cur}${o.shipping_fee.toLocaleString()}`} />}
                                  {(o.discount > 0) && <InfoRow label="Discount" value={`-${cur}${o.discount.toLocaleString()}`} />}
                                  <InfoRow label="Total" value={`${cur}${(o.grand_total || o.total || 0).toLocaleString()}`} bold />
                                </div>
                                {o.notes && (
                                  <div className="border-t pt-2 mt-2">
                                    <InfoRow label="Notes" value={o.notes} />
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="p-8 text-center text-gray-400">
            {hasFilters ? 'No orders match your filters' : 'No orders yet'}
          </div>
        )}
      </div>
    </div>
  );
}

function InfoRow({ label, value, mono, bold }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-gray-500 shrink-0">{label}</span>
      <span className={`text-right truncate ${mono ? 'font-mono' : ''} ${bold ? 'font-bold text-gray-900' : 'text-gray-800'}`}>{value}</span>
    </div>
  );
}
